import { useEffect, useState } from "react";
import { Modal, Input, Button, Table, DatePicker, Select, Tooltip } from "antd";
import { DeleteOutlined, WarningOutlined } from "@ant-design/icons";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import trans from "../../../utils";
import { useBulkAssignServiceRequestsMutation } from "../../../Redux/service/Dispatcher/Dispatcher";
import { useLocalForms } from "../../../hooks/useForms";

const DEFAULT_DURATION_MIN = 120; // 2 hours, per story AC
// Antd's standard error/danger background tint — used to flag rows whose
// preferred visit window has already passed and can't be assigned as-is.
const EXPIRED_ROW_BACKGROUND = "#fff1f0";

function isRowExpired(record) {
  return !!record.preferredVisitTimeEnd && dayjs().isAfter(dayjs(record.preferredVisitTimeEnd));
}

// Only dates inside the request's preferred visit window are selectable —
// everything else is disabled on the calendar. Requests without a preferred
// window at all are left unrestricted.
function disabledDateFor(record) {
  return (current) => {
    if (!current || !record.preferredVisitTime) return false;
    const start = dayjs(record.preferredVisitTime).startOf("day");
    const end = dayjs(record.preferredVisitTimeEnd || record.preferredVisitTime).endOf("day");
    return current.isBefore(start, "day") || current.isAfter(end, "day");
  };
}

// Restrict the time portion on the start/end day of the preferred window so
// the dispatcher cannot pick e.g. 24 Aug 12:00 AM when the window starts at
// 24 Aug 05:00 PM.
function disabledTimeFor(record) {
  return (date) => {
    if (!date || !record.preferredVisitTime) return {};
    const start = dayjs(record.preferredVisitTime);
    const end = dayjs(record.preferredVisitTimeEnd || record.preferredVisitTime).endOf("day");
    const isStartDay = date.isSame(start, "day");
    const isEndDay = date.isSame(end, "day");

    const disabledHours = () => {
      const hours = [];
      if (isStartDay) {
        for (let h = 0; h < start.hour(); h++) hours.push(h);
      }
      if (isEndDay) {
        for (let h = end.hour() + 1; h < 24; h++) hours.push(h);
      }
      return hours;
    };

    const disabledMinutes = (selectedHour) => {
      const minutes = [];
      if (isStartDay && selectedHour === start.hour()) {
        for (let m = 0; m < start.minute(); m++) minutes.push(m);
      }
      if (isEndDay && selectedHour === end.hour()) {
        for (let m = end.minute() + 1; m < 60; m++) minutes.push(m);
      }
      return minutes;
    };

    const disabledSeconds = (selectedHour, selectedMinute) => {
      const seconds = [];
      if (isStartDay && selectedHour === start.hour() && selectedMinute === start.minute()) {
        for (let s = 0; s < start.second(); s++) seconds.push(s);
      }
      if (isEndDay && selectedHour === end.hour() && selectedMinute === end.minute()) {
        for (let s = end.second() + 1; s < 60; s++) seconds.push(s);
      }
      return seconds;
    };

    return { disabledHours, disabledMinutes, disabledSeconds };
  };
}

// Task Start Date defaults to today when today falls inside the request's
// preferred visit window. If it's before the window, it defaults to the
// window start; if it's after the window, the request is expired and the
// cell is left empty.
function computeDefaultStartTime(row) {
  if (!row.preferredVisitTime) return dayjs().toISOString();
  const start = dayjs(row.preferredVisitTime);
  const end = dayjs(row.preferredVisitTimeEnd || row.preferredVisitTime).endOf("day");
  const now = dayjs();
  if (now.isBefore(start)) return start.toISOString();
  if (now.isAfter(end)) return "";
  return now.toISOString();
}

// Explains, for the warning icon's tooltip, why a row's Task Start Date
// conflicts with its preferred visit time.
function getConflictInfo(record) {
  if (isRowExpired(record)) {
    return {
      message: trans(
        "This request's preferred visit window has already passed and can't be assigned until it's updated from Call Center",
      ),
    };
  }
  if (record.preferredVisitTime && !record.startTime) {
    const start = dayjs(record.preferredVisitTime).format("D MMM, hh:mm A");
    const end = dayjs(record.preferredVisitTimeEnd || record.preferredVisitTime).format("D MMM, hh:mm A");
    return {
      message: `${trans("Today falls outside this request's preferred time")}: ${start} – ${end}`,
    };
  }
  return null;
}

function buildInitialRows(selectedRows, formOptions) {
  const defaultFormValues = formOptions.slice(0, 2).map((o) => o.value);
  return selectedRows.map((row) => ({
    id: row.id,
    requestNumber: row.requestNumber,
    location: trans(row.markaz || row.governorate) || "-",
    address: row.address || "-",
    product: row.product || "-",
    preferredVisitTime: row.preferredVisitTime,
    preferredVisitTimeEnd: row.preferredVisitTimeEnd,
    startTime: computeDefaultStartTime(row),
    duration: DEFAULT_DURATION_MIN,
    forms: defaultFormValues,
  }));
}

export default function ReviewAssignmentModal({
  open,
  onClose,
  onBack,
  technicianUserId,
  technicianName,
  notes,
  selectedRows,
  onAssigned,
}) {
  const [rows, setRows] = useState([]);
  const [bulkAssign, { isLoading: assigning }] = useBulkAssignServiceRequestsMutation();
  const { options: formOptions, isFetching: formsLoading } = useLocalForms();

  useEffect(() => {
    if (open) setRows(buildInitialRows(selectedRows, formOptions));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const updateRow = (id, patch) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const removeRow = (id) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const handleAssignee = async () => {
    if (rows.length === 0) {
      toast.error(trans("Please select at least one service request"), { autoClose: 2000 });
      return;
    }
    const expiredRow = rows.find(isRowExpired);
    if (expiredRow) {
      toast.error(
        trans(
          "One or more requests' preferred visit window has already passed and can't be assigned until it's updated from Call Center",
        ),
        { autoClose: 3000 },
      );
      return;
    }
    const missingTime = rows.find((r) => !r.startTime || !r.duration);
    if (missingTime) {
      toast.error(trans("Start time and duration are required for every request"), { autoClose: 2500 });
      return;
    }

    const outOfWindowRow = rows.find((r) => {
      if (!r.preferredVisitTime || !r.startTime) return false;
      const start = dayjs(r.preferredVisitTime);
      const end = dayjs(r.preferredVisitTimeEnd || r.preferredVisitTime).endOf("day");
      const selected = dayjs(r.startTime);
      return selected.isBefore(start) || selected.isAfter(end);
    });
    if (outOfWindowRow) {
      toast.error(trans("Start time must be within the preferred visit window"), { autoClose: 2500 });
      return;
    }

    const assignments = rows.map((r) => ({
      serviceRequestId: r.id,
      startTime: dayjs(r.startTime).toISOString(),
      expectedDuration: Number(r.duration),
      forms: r.forms,
    }));

    try {
      const result = await bulkAssign({
        assignments,
        technicianUserId,
        notes: notes || undefined,
      }).unwrap();
      const assignedTechnicianName = result?.data?.technician?.name || technicianName;
      const assignedCount = result?.data?.assignedCount ?? assignments.length;
      toast.success(
        `${assignedCount} ${trans("Service Requests successfully assigned to")} ${assignedTechnicianName}.`,
        { autoClose: 2500 },
      );
      onAssigned && onAssigned();
    } catch (error) {
      toast.error(trans(error?.data?.message) || trans("Failed to assign service requests"), {
        autoClose: 3000,
      });
    }
  };

  const columns = [
    { title: trans("Request"), dataIndex: "requestNumber", key: "requestNumber", width: 130 },
    { title: trans("Location"), dataIndex: "location", key: "location", width: 120 },
    { title: trans("Customer Address"), dataIndex: "address", key: "address", width: 160, ellipsis: true },
    { title: trans("Product"), dataIndex: "product", key: "product", width: 130 },
    {
      title: trans("Preferred Visit"),
      key: "preferredVisit",
      width: 170,
      render: (_, record) =>
        record.preferredVisitTime ? (
          <div>
            <div>{dayjs(record.preferredVisitTime).format("ddd, D MMM")}</div>
            <div className="neutral-400">
              {dayjs(record.preferredVisitTime).format("hh:mm A")}
              {record.preferredVisitTimeEnd ? ` – ${dayjs(record.preferredVisitTimeEnd).format("hh:mm A")}` : ""}
            </div>
          </div>
        ) : (
          "-"
        ),
    },
    {
      title: trans("Task Start Date"),
      key: "startTime",
      width: 250,
      render: (_, record) => {
        const conflict = getConflictInfo(record);
        return (
          <div className="d-flex align-items-center gap-2">
            <DatePicker
              showTime={{ format: "HH:mm" }}
              format="DD MMM YYYY, hh:mm A"
              style={{ width: "100%" }}
              value={record.startTime ? dayjs(record.startTime) : null}
              disabledDate={disabledDateFor(record)}
              disabledTime={disabledTimeFor(record)}
              onChange={(date) => updateRow(record.id, { startTime: date ? date.toISOString() : "" })}
              placeholder={trans("Select date & time")}
            />
            {conflict && (
              <Tooltip title={conflict.message}>
                <WarningOutlined style={{ color: "#faad14" }} />
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: trans("Estimated Duration"),
      key: "duration",
      width: 140,
      render: (_, record) => (
        <Input
          type="number"
          min={1}
          suffix={trans("Min")}
          value={record.duration}
          onChange={(e) => updateRow(record.id, { duration: e.target.value })}
        />
      ),
    },
    {
      title: trans("Forms"),
      key: "forms",
      width: 220,
      render: (_, record) => (
        <Select
          mode="multiple"
          allowClear
          style={{ width: "100%" }}
          placeholder={trans("Select forms")}
          loading={formsLoading}
          options={formOptions}
          value={record.forms}
          onChange={(val) => updateRow(record.id, { forms: val })}
        />
      ),
    },
    {
      title: trans("Actions"),
      key: "actions",
      width: 70,
      render: (_, record) => (
        <Button danger type="text" icon={<DeleteOutlined />} onClick={() => removeRow(record.id)} />
      ),
    },
  ];

  return (
    <Modal
      title={trans("Review Assignment")}
      open={open}
      onCancel={onClose}
      width={1100}
      footer={[
        <Button key="back" onClick={onBack}>
          {trans("Back")}
        </Button>,
        <Button key="assign" type="primary" loading={assigning} onClick={handleAssignee}>
          {trans("Assignee")}
        </Button>,
      ]}
    >
      <p className="fw-500 mb-3">
        {trans("Technician")} : {technicianName}
      </p>
      <Table
        rowKey="id"
        columns={columns}
        dataSource={rows}
        pagination={false}
        scroll={{ x: "max-content" }}
        onRow={(record) => ({
          style: isRowExpired(record) ? { background: EXPIRED_ROW_BACKGROUND } : undefined,
        })}
      />
    </Modal>
  );
}
